import { useQuery } from "@tanstack/react-query";
import type { AreaDensityResponse } from "@situate/contracts/runtime";
import {
  formatAnalyticsPeriodLabel,
  getTotalPopulationTrendQueryRange,
} from "@/features/deployment-dashboard/shared/analytics-query-range";
import type { DensityTrendPoint } from "@/features/deployment-dashboard/widgets/charts/density-trend/types";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";

const AREA_DENSITY_PATH = "/v1/analytics/area-density";

interface DensityTrendState {
  data: DensityTrendPoint[];
  loading: boolean;
  error: Error | null;
}

interface UseDensityTrendParams {
  deploymentId: string;
  /** Areas to aggregate; empty while areas are still loading. */
  areaIds: readonly string[];
  startDate: Date;
  endDate: Date;
  enabled?: boolean;
}

/**
 * Fetches the Analytics page's "Density Trend" chart data from
 * `situate_analytics.area_occupancy_statistics` via the runtime API's
 * `area-density` endpoint, for the selected areas and date range.
 */
export function useDensityTrend({
  deploymentId,
  areaIds,
  startDate,
  endDate,
  enabled = true,
}: UseDensityTrendParams): DensityTrendState {
  const runtime = useRuntimeHttpClient();
  const hasAreaDensityGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "analytics_area_density"
  );
  const client = runtime.client;
  const { granularity, startTime, endTime } = getTotalPopulationTrendQueryRange(startDate, endDate);

  const query = useQuery<DensityTrendPoint[]>({
    queryKey: ["density-trend", deploymentId, areaIds, startTime, endTime],
    queryFn: async ({ signal }) => {
      if (client == null) throw new Error("Runtime client is unavailable");

      const response = await client.post<AreaDensityResponse>(
        AREA_DENSITY_PATH,
        {
          area_ids: areaIds,
          start_time: startTime,
          end_time: endTime,
          granularity,
        },
        { signal }
      );

      return response.data_points.map((point) => ({
        period: formatAnalyticsPeriodLabel(point.timestamp, granularity),
        timestamp: point.timestamp,
        density: point.density,
      }));
    },
    enabled:
      Boolean(client) &&
      runtime.deploymentId === deploymentId &&
      hasAreaDensityGrant &&
      enabled &&
      areaIds.length > 0,
  });

  return {
    data: query.data ?? [],
    loading: query.isLoading,
    error: query.error,
  };
}
